import m from 'mithril'
import { css } from '@emotion/css'
import { EditorState } from 'prosemirror-state'
import { EditorView } from 'prosemirror-view'
import { Decoration, DecorationSet } from 'prosemirror-view'
import { Plugin } from 'prosemirror-state'
import { colors, font, space } from '../styles/theme.js'
import { parseMessages } from './parse.js'

const editorWrap = css`
  .ProseMirror {
    outline: none;
    font-size: ${font.size.base};
    line-height: 1.7;
  }

  .ProseMirror p {
    margin-bottom: ${space.sm};
  }

  .ProseMirror pre {
    background: #282c34;
    color: #abb2bf;
    border-radius: 6px;
    padding: ${space.md};
    margin: ${space.sm} 0;
    overflow-x: auto;
    font-family: 'SF Mono', 'Fira Code', monospace;
    font-size: ${font.size.sm};
    line-height: 1.5;
  }

  .ProseMirror code {
    background: rgba(0, 0, 0, 0.06);
    border-radius: 3px;
    padding: 0.1em 0.3em;
    font-family: 'SF Mono', 'Fira Code', monospace;
    font-size: 0.9em;
  }

  .ProseMirror pre code {
    background: none;
    padding: 0;
    border-radius: 0;
  }

  .ProseMirror ul, .ProseMirror ol {
    padding-left: 1.5em;
    margin: ${space.sm} 0;
  }

  .ProseMirror li {
    margin-bottom: 0.25em;
  }

  .ProseMirror a {
    color: ${colors.brand};
    text-decoration: underline;
  }

  .ProseMirror strong {
    font-weight: ${font.weight.semibold};
  }

  .chat-message {
    padding: ${space.md} ${space.lg};
    margin-bottom: 1px;
    position: relative;
  }

  .chat-role-user {
    background: ${colors.msgUser};
  }

  .chat-role-assistant {
    background: ${colors.msgAssistant};
  }

  .chat-role-system {
    background: ${colors.msgSystem};
    font-size: ${font.size.sm};
    color: ${colors.textSecondary};
  }

  .chat-role-unknown {
    background: ${colors.msgAssistant};
  }

  .hypo-highlight {
    border-radius: 2px;
    cursor: pointer;
    transition: opacity 0.1s ease;
  }
  .hypo-highlight:hover {
    opacity: 0.8;
  }
  .hypo-highlight-masked {
    font-family: 'SF Mono', 'Fira Code', monospace;
    font-size: 0.85em;
    letter-spacing: 0.02em;
    user-select: none;
  }
`

const roleLabel = css`
  font-size: ${font.size.xs};
  font-weight: ${font.weight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: ${space.xs};
  user-select: none;
`

// Map text-content offsets to ProseMirror positions within a message node.
// messagePos = position of the message node in the doc.
// We walk descendants to find where offset falls in the text stream.
export function textOffsetToPos(messageNode, messagePos, textOffset) {
  let charsSeen = 0
  let result = null

  messageNode.descendants((node, pos) => {
    if (result !== null) return false
    if (node.isText) {
      const nodeStart = charsSeen
      const nodeEnd = charsSeen + node.nodeSize
      if (textOffset >= nodeStart && textOffset <= nodeEnd) {
        // pos is relative to messageNode content; +1 for the message node open tag
        result = messagePos + 1 + pos + (textOffset - nodeStart)
        return false
      }
      charsSeen += node.nodeSize
    }
  })

  if (result === null) {
    result = messagePos + messageNode.nodeSize - 1
  }

  return result
}

// Inverse: PM doc position → text-content offset within a message node.
export function posToTextOffset(messageNode, messagePos, docPos) {
  let charsSeen = 0
  let result = null
  const relPos = docPos - messagePos - 1

  messageNode.descendants((node, pos) => {
    if (result !== null) return false
    if (node.isText) {
      if (relPos >= pos && relPos <= pos + node.nodeSize) {
        result = charsSeen + (relPos - pos)
        return false
      }
      charsSeen += node.nodeSize
    }
  })

  return result ?? charsSeen
}

// Find which message node contains a doc position.
export function findMessageAt(doc, docPos) {
  let result = null
  doc.descendants((node, pos) => {
    if (result) return false
    if (node.type.name === 'message') {
      if (docPos >= pos && docPos <= pos + node.nodeSize) {
        result = { node, pos, attrs: node.attrs }
      }
      return false
    }
  })
  return result
}

// Build ProseMirror DecorationSet from highlights array.
// highlights: [{ id, message_id, start_offset, end_offset, type_color, ... }]
// messages: original messages array (to map message_id → sequence)
// doc: ProseMirror doc node
export function buildHighlightDecorations(doc, highlights, messages) {
  if (!highlights || highlights.length === 0) return DecorationSet.empty

  const msgSeqMap = new Map()
  for (const msg of messages) {
    msgSeqMap.set(msg.id, msg.sequence)
  }

  const seqPosMap = new Map()
  doc.descendants((node, pos) => {
    if (node.type.name === 'message') {
      seqPosMap.set(node.attrs.sequence, { node, pos })
      return false
    }
  })

  const decorations = []
  for (const hl of highlights) {
    const seq = msgSeqMap.get(hl.message_id)
    if (seq == null) continue
    const entry = seqPosMap.get(seq)
    if (!entry) continue

    const from = textOffsetToPos(entry.node, entry.pos, hl.start_offset)
    const to = textOffsetToPos(entry.node, entry.pos, hl.end_offset)

    if (from != null && to != null && from < to) {
      const isMasking = hl.masks_content
      decorations.push(Decoration.inline(from, to, {
        class: isMasking ? 'hypo-highlight hypo-highlight-masked' : 'hypo-highlight',
        style: isMasking
          ? `background: ${hl.type_color}55; border-radius: 3px;`
          : `background: ${hl.type_color}33; border-bottom: 2px solid ${hl.type_color};`,
        'data-highlight-id': hl.id,
      }))
    }
  }

  return DecorationSet.create(doc, decorations)
}

function highlightPlugin(initialDecos, onHighlightClick) {
  return new Plugin({
    state: {
      init() { return initialDecos },
      apply(tr, decos) {
        if (tr.docChanged) return decos.map(tr.mapping, tr.doc)
        const newDecos = tr.getMeta('highlightDecos')
        if (newDecos) return newDecos
        return decos
      },
    },
    props: {
      decorations(state) {
        return this.getState(state)
      },
      handleClick(view, pos, event) {
        const target = event.target
        if (target && target.classList && target.classList.contains('hypo-highlight')) {
          const hlId = target.getAttribute('data-highlight-id')
          if (hlId && onHighlightClick) {
            onHighlightClick(hlId)
            return true
          }
        }
        return false
      },
    },
  })
}

// Mithril closure component wrapping a ProseMirror EditorView.
// Props:
//   messages    — array of { id, role, content, sequence, timestamp }
//   highlights  — array of highlight objects from API
//   onTextSelected(messageId, startOffset, endOffset, rect)
//   onHighlightClicked(highlightId)
//   onMessageClicked(messageId)  — fired on click when no text is selected
export function ChatRenderer() {
  let view = null
  let containerEl = null

  function createView(messages, highlights, onTextSelected, onHighlightClicked, onMessageClicked) {
    if (view) {
      view.destroy()
      view = null
    }
    if (!containerEl || !messages) return

    containerEl.innerHTML = ''

    const doc = parseMessages(messages)
    const decoSet = buildHighlightDecorations(doc, highlights || [], messages)
    const plugin = highlightPlugin(decoSet, onHighlightClicked)

    const state = EditorState.create({
      doc,
      plugins: [plugin],
    })

    view = new EditorView(containerEl, {
      state,
      editable: () => false,
      dispatchTransaction(tr) {
        const newState = view.state.apply(tr)
        view.updateState(newState)
      },
      handleDOMEvents: {
        mouseup: (_view, event) => {
          if (!onTextSelected) return false
          const sel = window.getSelection()
          if (!sel || sel.isCollapsed || !sel.rangeCount) return false

          const { from, to } = _view.state.selection
          if (from === to) return false

          const msgStart = findMessageAt(_view.state.doc, from)
          const msgEnd = findMessageAt(_view.state.doc, to)
          if (!msgStart || !msgEnd) return false

          // Only allow highlights within a single message
          if (msgStart.pos !== msgEnd.pos) return false

          const startOffset = posToTextOffset(msgStart.node, msgStart.pos, from)
          const endOffset = posToTextOffset(msgEnd.node, msgEnd.pos, to)

          if (startOffset != null && endOffset != null && startOffset < endOffset) {
            const seq = msgStart.attrs.sequence
            const msg = messages.find(m => m.sequence === seq)
            if (msg) {
              const range = sel.getRangeAt(0)
              const rect = range.getBoundingClientRect()
              onTextSelected(msg.id, startOffset, endOffset, rect)
            }
          }
          return false
        },
        click: (_view, event) => {
          if (!onMessageClicked) return false
          // Don't fire on text selection or highlight clicks
          const sel = window.getSelection()
          if (sel && !sel.isCollapsed) return false
          if (event.target.classList && event.target.classList.contains('hypo-highlight')) return false

          const pos = _view.posAtCoords({ left: event.clientX, top: event.clientY })
          if (!pos) return false
          const msg = findMessageAt(_view.state.doc, pos.pos)
          if (!msg) return false
          const seq = msg.attrs.sequence
          const matched = messages.find(m => m.sequence === seq)
          if (matched) onMessageClicked(matched.id)
          return false
        },
      },
    })

    // Inject role labels into each message node
    const messageDivs = containerEl.querySelectorAll('.chat-message')
    messageDivs.forEach((div) => {
      const role = div.getAttribute('data-role')
      if (role) {
        const labelEl = document.createElement('div')
        labelEl.className = roleLabel
        labelEl.style.color = role === 'user' ? colors.brandDark
          : role === 'assistant' ? colors.textSecondary
          : colors.textMuted
        labelEl.textContent = role
        div.insertBefore(labelEl, div.firstChild)
      }
    })
  }

  function updateDecorations(messages, highlights) {
    if (!view) return
    const doc = view.state.doc
    const decoSet = buildHighlightDecorations(doc, highlights || [], messages)
    const tr = view.state.tr.setMeta('highlightDecos', decoSet)
    view.dispatch(tr)
  }

  return {
    oncreate(vnode) {
      containerEl = vnode.dom
      const { messages, highlights, onTextSelected, onHighlightClicked, onMessageClicked } = vnode.attrs
      createView(messages, highlights, onTextSelected, onHighlightClicked, onMessageClicked)
    },

    onupdate(vnode) {
      const { messages, highlights, onTextSelected, onHighlightClicked, onMessageClicked } = vnode.attrs
      if (messages !== vnode.state._prevMessages) {
        createView(messages, highlights, onTextSelected, onHighlightClicked, onMessageClicked)
      } else if (highlights !== vnode.state._prevHighlights) {
        updateDecorations(messages, highlights)
      }
      vnode.state._prevMessages = messages
      vnode.state._prevHighlights = highlights
    },

    onremove() {
      if (view) {
        view.destroy()
        view = null
      }
    },

    view(vnode) {
      vnode.state._prevMessages = vnode.attrs.messages
      vnode.state._prevHighlights = vnode.attrs.highlights
      return m('div', { class: editorWrap })
    },
  }
}
